import { groupBy } from "lodash-es";

import * as Cmp from "../comparison";

import { DataExtractor, DataExtractorGroup, cached } from "./extractor";
import { sorted } from "./sorted";
import { NodeDescriptor, MultiTreeDataSource } from "./source";
import { DataView, Sorter, getExtractors, Expando } from "./view";

class PivotDataView<ITEM> implements DataView<
    NodeDescriptor<Group<ITEM, any>>
> {
    readonly dataSource: MultiTreeDataSource<Group<ITEM, any>>;
    readonly extractorGroups: Array<
        DataExtractorGroup<NodeDescriptor<Group<ITEM, any>>>
    >;
    readonly sorters: Array<Sorter>;
    readonly defaultCompareItems: Cmp.ComparisonFunction<
        NodeDescriptor<Group<ITEM, any>>
    >;

    constructor(
        baseDataView: DataView<ITEM>,
        pivotExtractorIds: Array<string>,
        aggregateExtractorGroups: Array<
            DataExtractorGroup<NodeDescriptor<Group<ITEM, any>>>
        >
    ) {
        /* We do support pivoting with 0 extarctors.
        if (pivotExtractorIds.length === 0) {
            throw new Error("No pivot extractor id given");
        }
        */
        // Split the view's extractors into two sets: the extractors used for
        // the pivot, and the rest
        const pivotExtractors = getExtractors(baseDataView, pivotExtractorIds);
        // Sort the data for easy grouping
        const sorters = pivotExtractorIds.map((extractorId) => {
            const sorter: Sorter = {
                extractorId,
                direction: "ASC",
            };
            return sorter;
        });
        const sortedView = sorted(baseDataView, sorters);
        const iter = sortedView.dataSource.iter();
        // Given extractors for fields Company/Department
        //
        // If we have a (sorted) set of data like so:
        // [
        //    {Company: "Impero", Department: "Legal", Name: "Employee 1"},
        //    {Company: "Impero", Department: "Legal", Name: "Employee 4"},
        //    {Company: "Impero", Department: "IT", Name: "Employee 2"},
        //    {Company: "JBM", Department: "Legal", Name: "Employee 1"},
        //    {Company: "JBM", Department: "Legal", Name: "Employee 2"},
        // ]
        // We will end up with groups like
        // [
        //   {
        //     id: "/0",
        //     groupingKey: "Impero",
        //     items: c[
        //       {Company: "Impero", Department: "Legal", Name: "Employee 1"},
        //       {Company: "Impero", Department: "Legal", Name: "Employee 4"},
        //       {Company: "Impero", Department: "IT", Name: "Employee 2"},
        //     ]
        //     subGroups: [
        //       {
        //         id: "/0/1",
        //         groupingKey: "Legal",
        //         items: [
        //           {Company: "Impero", Department: "Legal", Name: "Employee 1"},
        //           {Company: "Impero", Department: "Legal", Name: "Employee 4"},
        //         ],
        //         subGroups: [],
        //       },
        //       {
        //         id: "/0/2",
        //         groupingKey: "IT",
        //         items: [
        //           {Company: "Impero", Department: "IT", Name: "Employee 2"},
        //         ],
        //         subGroups: [],
        //       }
        //     ],
        //   }
        //   {
        //     id: "/1",
        //     groupingKey: "JBM",
        //     items: [
        //       {Company: "JBM", Department: "Legal", Name: "Employee 1"},
        //     ]
        //     subGroups: [
        //       {
        //         id: "/1/0",
        //         groupingKey: "Legal",
        //         items: [
        //           {Company: "JBM", Department: "Legal", Name: "Employee 1"},
        //         ],
        //         subGroups: [],
        //       }
        //     ]
        //   }
        // ]
        const topLevelGroups: Array<Group<ITEM, any>> = [];
        // A flattened list of the current groups, like
        // [ { id: "1", groupingKey: "JBM", ...}, { id: "1.0", groupingKey: "Legal"}]
        let flatGroups: Array<Group<ITEM, any>> = [];
        const allItems = [];
        while (true) {
            const item = iter.next();
            if (item === undefined) {
                break;
            }
            for (let level = 0; level < pivotExtractors.length; level++) {
                const pivotExtractor = pivotExtractors[level];
                const groupingKey = pivotExtractor.getValue(item);
                if (
                    flatGroups.length <= level ||
                    pivotExtractor.compare(flatGroups[level].items[0], item) !==
                        0
                ) {
                    let parentGroup: Group<ITEM, any> | undefined;
                    let id: string;
                    if (level > 0) {
                        parentGroup = flatGroups[level - 1];
                        id =
                            parentGroup.id +
                            "/" +
                            (parentGroup.subGroups.length + 1);
                    } else {
                        id = "/" + topLevelGroups.length;
                        parentGroup = undefined;
                    }
                    const currentGroup: Group<ITEM, any> = {
                        id,
                        groupingKey,
                        items: [],
                        subGroups: [],
                    };
                    if (level === 0) {
                        topLevelGroups.push(currentGroup);
                    }
                    if (parentGroup) {
                        parentGroup.subGroups.push(currentGroup);
                    }
                    // If we change groups at level N, we need to remove groups
                    // at level N+x from flatGroups, so that we generate new
                    // groups at the next iteration of the loop
                    flatGroups = flatGroups.slice(0, level + 1);
                    flatGroups[level] = currentGroup;
                }
            }
            allItems.push(item);
            flatGroups.forEach((group) => {
                group.items.push(item);
            });
        }
        const rootGroup = {
            id: "/",
            groupingKey: "",
            subGroups: topLevelGroups,
            items: allItems,
        };
        this.dataSource = new MultiTreeDataSource<Group<ITEM, any>>(
            [rootGroup],
            (group) => group.subGroups,
            (group) => group.id,
            (group, level) => pivotExtractors[level].asString(group.items[0]),
            undefined,
            rootGroup.items.length
        );
        const pivotExtractor = new GroupingDataExtractor(pivotExtractors);
        const pivotExtractorsGroup: DataExtractorGroup<
            NodeDescriptor<Group<ITEM, any>>
        > = {
            id: "__pivot",
            label: "Pivot",
            extractors: [pivotExtractor],
        };
        this.extractorGroups = [pivotExtractorsGroup].concat(
            aggregateExtractorGroups
        );
        this.sorters = [{ extractorId: pivotExtractor.id, direction: "ASC" }];
        // groups are already unique
        this.defaultCompareItems = () => 0;
    }
}

class GroupingDataExtractor<ITEM> implements DataExtractor<
    NodeDescriptor<Group<ITEM, any>>,
    any
> {
    readonly id: string;
    readonly label: string;
    readonly extractors: Array<DataExtractor<ITEM, any>>;
    constructor(extractors: Array<DataExtractor<ITEM, any>>) {
        this.id = "/" + extractors.map((ext) => ext.id).join("/");
        this.label = extractors.map((ext) => ext.label).join(" / ");
        this.extractors = extractors;
    }
    getValue(nodeDesc: NodeDescriptor<Group<ITEM, any>>) {
        // ancestors are like ["root", "/0"]
        if (nodeDesc.ancestors.length === 0) {
            // We're on the fake root group
            return null;
        }
        return nodeDesc.value.groupingKey;
    }

    compare(
        nodeDesc1: NodeDescriptor<Group<ITEM, any>>,
        nodeDesc2: NodeDescriptor<Group<ITEM, any>>
    ): number {
        // Don't attempt to compare values from the 'root' group, rely on the index
        if (
            nodeDesc1.ancestors.length > 0 &&
            nodeDesc2.ancestors.length > 0 &&
            nodeDesc1.ancestors.length === nodeDesc2.ancestors.length &&
            nodeDesc1.ancestors[nodeDesc1.ancestors.length - 1] ===
                nodeDesc2.ancestors[nodeDesc2.ancestors.length - 1]
        ) {
            const nodeLevel = nodeDesc1.ancestors.length - 1;
            const dataExtractor = this.extractors[nodeLevel];
            const result = dataExtractor.compare(
                nodeDesc1.value.items[0],
                nodeDesc2.value.items[0]
            );
            // If the compare function returns 0, fallback on index-based
            // comparison (stable sort)
            if (result !== 0) {
                return result;
            }
        }
        if (nodeDesc1.index === nodeDesc2.index) {
            return 0;
        } else {
            return nodeDesc1.index < nodeDesc2.index ? -1 : 1;
        }
    }
    asString(nodeDesc: NodeDescriptor<Group<ITEM, any>>) {
        const nodeLevel = nodeDesc.ancestors.length - 1;
        const dataExtractor = this.extractors[nodeLevel];
        if (nodeDesc.ancestors.length === 0) {
            // We're on the fake root group
            return "";
        }
        return dataExtractor.asString(nodeDesc.value.items[0]);
    }
}

interface Group<ITEM, GroupingKey> {
    id: string;
    groupingKey: GroupingKey;
    subGroups: Array<Group<ITEM, any>>;
    items: Array<ITEM>;
}

function pivoted<ITEM>(
    dataView: DataView<ITEM>,
    pivotExtractorIds: Array<string>,
    aggregateExtractorGroups: Array<
        DataExtractorGroup<NodeDescriptor<Group<ITEM, any>>>
    >
) {
    return new PivotDataView(
        dataView,
        pivotExtractorIds,
        aggregateExtractorGroups
    );
}

interface ExtractorAggregate<ITEM, VAL> {
    extractorId: string;
    aggregateId: string;
    aggregateLabel: string;
    countValue: (value: VAL, item: ITEM) => number;
    aggregateCount?: (count: number, itemsWithValue: [ITEM, VAL][]) => number;
}

function countAggregate<ITEM, VAL>(
    aggregate: ExtractorAggregate<ITEM, VAL>,
    itemsWithValue: [ITEM, VAL][]
): number {
    const count = itemsWithValue.reduce(
        (memo, [item, value]) => memo + aggregate.countValue(value, item),
        0
    );

    if (aggregate.aggregateCount === undefined) {
        return count;
    }

    return aggregate.aggregateCount(count, itemsWithValue);
}

class AggregateExtractorsBuilder<ITEM> {
    private readonly aggregatesByExtractorId: {
        [extractorId: string]: Array<ExtractorAggregate<ITEM, any>>;
    };

    constructor(aggregatesByExtractorId: {
        [extractorId: string]: Array<ExtractorAggregate<ITEM, any>>;
    }) {
        this.aggregatesByExtractorId = aggregatesByExtractorId;
    }

    buildUniqueItemsExtractor(
        summarizeBy: SummarizeBy,
        getSummaryId: (item: ITEM) => string
    ): (group: Group<ITEM, any>) => Array<ITEM> {
        if (summarizeBy === "NONE") {
            let last:
                | {
                      group: Group<ITEM, any>;
                      items: Array<ITEM>;
                  }
                | undefined;
            return (group) => {
                if (last && group === last.group) {
                    return last.items;
                } else {
                    const seenItemIds: { [id: string]: boolean } = {};
                    const items: Array<ITEM> = group.items.filter((item) => {
                        const itemId = getSummaryId(item);
                        const seen = seenItemIds[itemId];
                        if (seen) {
                            return false;
                        } else {
                            seenItemIds[itemId] = true;
                            return true;
                        }
                    });
                    last = { group, items };
                    return items;
                }
            };
        } else {
            return (group) => group.items;
        }
    }

    buildExtractors(
        dataView: DataView<ITEM>,
        summarizeBy: SummarizeBy,
        getSummaryId: (item: ITEM) => string
    ): Array<DataExtractorGroup<NodeDescriptor<Group<ITEM, any>>>> {
        let aggregateCount = 0;
        const getGroupItems = this.buildUniqueItemsExtractor(
            summarizeBy,
            getSummaryId
        );
        const mappedExtractorGroups = dataView.extractorGroups.map((group) => {
            const mappedExtractors: Array<
                DataExtractor<NodeDescriptor<Group<ITEM, any>>, number>
            > = [];
            group.extractors.forEach((ext) => {
                const aggregates = this.aggregatesByExtractorId[ext.id];
                if (aggregates) {
                    aggregateCount += aggregates.length;
                    aggregates.forEach((agg) => {
                        const getAggregateValueFromGroup = cached(
                            (group: Group<ITEM, any>) => {
                                const items = getGroupItems(group);
                                const itemsWithValue: [ITEM, any][] = items.map(
                                    (item) => [item, ext.getValue(item)]
                                );
                                return countAggregate(agg, itemsWithValue);
                            },
                            (group) => group.id
                        );
                        const getAggregateValue = (
                            nodeDesc: NodeDescriptor<Group<ITEM, any>>
                        ) => getAggregateValueFromGroup(nodeDesc.value);
                        mappedExtractors.push({
                            id: agg.aggregateId,
                            label: agg.aggregateLabel,
                            getValue: getAggregateValue,
                            compare: (
                                nodeDesc1: NodeDescriptor<Group<ITEM, any>>,
                                nodeDesc2: NodeDescriptor<Group<ITEM, any>>
                            ) => {
                                // Same node desc
                                if (nodeDesc1.index === nodeDesc2.index) {
                                    return 0;
                                }
                                // If nodes are siblings, compare their aggregate values
                                if (
                                    nodeDesc1.ancestors.length > 0 &&
                                    nodeDesc2.ancestors.length > 0 &&
                                    nodeDesc1.ancestors.length ===
                                        nodeDesc2.ancestors.length &&
                                    nodeDesc1.ancestors[
                                        nodeDesc1.ancestors.length - 1
                                    ] ===
                                        nodeDesc2.ancestors[
                                            nodeDesc2.ancestors.length - 1
                                        ]
                                ) {
                                    const v1 = getAggregateValue(nodeDesc1);
                                    const v2 = getAggregateValue(nodeDesc2);
                                    const result = v1 - v2;
                                    // If the compare function returns 0, fallback on index-based
                                    // comparison (stable sort)
                                    if (result !== 0) {
                                        return result;
                                    } else {
                                        return (
                                            nodeDesc1.index - nodeDesc2.index
                                        );
                                    }
                                }
                                // If nodes are not siblings, compare all their ancestors
                                for (
                                    let i = 1;
                                    i < nodeDesc1.ancestors.length &&
                                    i < nodeDesc2.ancestors.length;
                                    i++
                                ) {
                                    const v1 = getAggregateValueFromGroup(
                                        nodeDesc1.ancestors[i]
                                    );
                                    const v2 = getAggregateValueFromGroup(
                                        nodeDesc2.ancestors[i]
                                    );
                                    const result = v1 - v2;
                                    if (result !== 0) {
                                        return result;
                                    }
                                }
                                // nodeDesc1 is an ancestor of nodeDesc2 or
                                // vice-versa. If nodeDesc1 is an ancestor of
                                // nodeDesc2, nodeDesc1 needs to be first
                                const result =
                                    nodeDesc1.ancestors.length -
                                    nodeDesc2.ancestors.length;
                                if (result !== 0) {
                                    return result;
                                }
                                return nodeDesc1.index - nodeDesc2.index;
                            },
                            asString: (
                                nodeDesc: NodeDescriptor<Group<ITEM, any>>
                            ) => getAggregateValue(nodeDesc).toString(),
                        });
                    });
                }
            });
            return {
                id: group.id,
                label: group.label,
                extractors: mappedExtractors,
            };
        });
        if (
            aggregateCount !==
            Object.values(this.aggregatesByExtractorId).reduce(
                (memo, aggs) => memo + aggs.length,
                0
            )
        ) {
            throw new Error(
                "Some of the aggregates do not match the extractors in the original view"
            );
        }
        return mappedExtractorGroups.filter(
            (group) => group.extractors.length > 0
        );
    }
}

function aggregate<ITEM>(
    extractorAggregates: Array<ExtractorAggregate<ITEM, any>>
) {
    // Hopefully this conserves the order in groups.
    const aggregatesByExtractorId = groupBy(
        extractorAggregates,
        (agg) => agg.extractorId
    );
    return new AggregateExtractorsBuilder(aggregatesByExtractorId);
}

function expandedAggregates<ITEM>(
    extractorAggregates: Array<ExtractorAggregate<ITEM, any>>
): Array<ExtractorAggregate<Expando<ITEM>, any>> {
    return extractorAggregates.map((agg) => ({
        extractorId: agg.extractorId,
        aggregateId: agg.aggregateId,
        aggregateLabel: agg.aggregateLabel,
        countValue: (value: any, item: Expando<ITEM>) =>
            agg.countValue(value, item.baseItem),
        aggregateCount: (count: number, items: [Expando<ITEM>, any][]) => {
            if (agg.aggregateCount === undefined) {
                return count;
            }
            return agg.aggregateCount(
                count,
                items.map((i) => [i[0].baseItem, i[1]])
            );
        },
    }));
}

type SummarizeBy = "NONE" | "BOTTOM_UP";

export {
    pivoted,
    PivotDataView,
    GroupingDataExtractor,
    type Group,
    aggregate,
    type ExtractorAggregate,
    expandedAggregates,
    type SummarizeBy,
    countAggregate,
};
